import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Users, 
  Gift, 
  Phone, 
  Ticket,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  X,
  Plus,
  DollarSign,
  CreditCard,
  Check,
  Search,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const CreateRifaClientes = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedClientes, setSelectedClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('numeros_desc'); // numeros_desc | numeros_asc | nombre_asc | monto_desc
  
  // Form data
  const [title, setTitle] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [prizes, setPrizes] = useState(['']);
  const [prizeImages, setPrizeImages] = useState([]);
  const [logo, setLogo] = useState(null);
  const [paymentAlias, setPaymentAlias] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  
  // Result
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    try {
      const response = await axios.get(`${API}/clientes/pagados`);
      setClientes(response.data);
    } catch (error) {
      console.error('Error fetching clientes:', error);
      toast.error('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  const toggleCliente = (clienteId) => {
    setSelectedClientes(prev => 
      prev.includes(clienteId)
        ? prev.filter(id => id !== clienteId)
        : [...prev, clienteId]
    );
  };

  // Filtra y ordena la lista segun searchTerm y sortBy
  const getVisibleClientes = () => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = term
      ? clientes.filter(c =>
          (c.nombre || '').toLowerCase().includes(term) ||
          (c.telefono || '').toLowerCase().includes(term)
        )
      : [...clientes];

    switch (sortBy) {
      case 'numeros_asc':
        return filtered.sort((a, b) => (a.numeros_pagados || 0) - (b.numeros_pagados || 0));
      case 'nombre_asc':
        return filtered.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
      case 'monto_desc':
        return filtered.sort((a, b) => (b.monto_total_pagado || 0) - (a.monto_total_pagado || 0));
      case 'numeros_desc':
      default:
        return filtered.sort((a, b) => (b.numeros_pagados || 0) - (a.numeros_pagados || 0));
    }
  };

  const selectAll = () => {
    // Selecciona solo los actualmente visibles (respeta filtro), sumandolos a los ya seleccionados
    const visibleIds = getVisibleClientes().map(c => c.id);
    setSelectedClientes(prev => Array.from(new Set([...prev, ...visibleIds])));
  };

  const deselectAll = () => {
    setSelectedClientes([]);
  };

  const addPrize = () => {
    setPrizes([...prizes, '']);
  };

  const removePrize = (index) => {
    if (prizes.length > 1) {
      setPrizes(prizes.filter((_, i) => i !== index));
    }
  };

  const updatePrize = (index, value) => {
    const newPrizes = [...prizes];
    newPrizes[index] = value;
    setPrizes(newPrizes);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrizeImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPrizeImages(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePrizeImage = (index) => {
    setPrizeImages(prev => prev.filter((_, i) => i !== index));
  };

  const getTotalNumeros = () => {
    return clientes
      .filter(c => selectedClientes.includes(c.id))
      .reduce((sum, c) => sum + (c.numeros_pagados || 0), 0);
  };

  const getTotalRecaudacion = () => {
    const price = parseFloat(unitPrice) || 0;
    return getTotalNumeros() * price;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedClientes.length === 0) {
      toast.error('Selecciona al menos un cliente');
      return;
    }
    
    if (!title.trim()) {
      toast.error('Ingresa un título para la rifa');
      return;
    }
    
    if (!unitPrice || parseFloat(unitPrice) <= 0) {
      toast.error('Ingresa un precio válido por número');
      return;
    }
    
    if (!prizes[0]?.trim()) {
      toast.error('Ingresa al menos un premio');
      return;
    }
    
    if (!paymentAlias.trim()) {
      toast.error('Ingresa el alias de pago');
      return;
    }
    
    if (!whatsappNumber.trim()) {
      toast.error('Ingresa un número de WhatsApp');
      return;
    }
    
    setCreating(true);
    
    try {
      const response = await axios.post(`${API}/raffles/clientes`, {
        title: title.trim(),
        unit_price: parseFloat(unitPrice),
        prizes: prizes.filter(p => p.trim()),
        prize_images: prizeImages,
        logo,
        payment_alias: paymentAlias.trim(),
        whatsapp_number: whatsappNumber.trim(),
        clientes_ids: selectedClientes
      });
      
      setResult(response.data);
      toast.success('¡Rifa creada exitosamente!');
    } catch (error) {
      console.error('Error creating rifa:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la rifa');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="animate-spin mx-auto mb-4" size={40} />
        <p>Cargando clientes...</p>
      </div>
    );
  }

  // Mostrar resultado si se creó
  if (result) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/admin')}>
            <ArrowLeft className="mr-2" size={18} />
            Volver al Dashboard
          </Button>
        </div>

        <Card className="bg-green-50 border-green-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 size={24} />
              ¡Rifa para Clientes Creada!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Título:</p>
                <p className="font-bold">{result.raffle.title}</p>
              </div>
              <div>
                <p className="text-gray-600">Total Clientes:</p>
                <p className="font-bold">{result.total_clientes}</p>
              </div>
              <div>
                <p className="text-gray-600">Total Números:</p>
                <p className="font-bold text-green-600">{result.total_numeros}</p>
              </div>
              <div>
                <p className="text-gray-600">Valor número/fila:</p>
                <p className="font-bold text-purple-600">${result.raffle.unit_price.toLocaleString('es-AR')}</p>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Números Asignados por Cliente:</h4>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {result.participantes.map((p, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{p.nombre}</span>
                      <span className="text-gray-500 text-sm">({p.telefono})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{p.numeros_pagados} números:</span>
                      <span className="font-mono text-green-600 font-bold">
                        {p.numeros_asignados[0]} - {p.numeros_asignados[p.numeros_asignados.length - 1]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/admin')}>
          <ArrowLeft className="mr-2" size={18} />
          Volver
        </Button>
        <div>
          <h2 className="text-3xl font-bold">Crear Rifa para Clientes</h2>
          <p className="text-gray-600">Rifa paga con números proporcionales a compras anteriores</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Columna izquierda - Datos de la rifa */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Datos de la Rifa</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Título de la Rifa *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ej: Rifa Especial para Clientes VIP"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="unitPrice">Valor número/fila *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      id="unitPrice"
                      type="text"
                      inputMode="decimal"
                      value={unitPrice}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                        setUnitPrice(v);
                      }}
                      placeholder="150000"
                      className="pl-8"
                      data-testid="unit-price"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="paymentAlias">Alias de Pago *</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      id="paymentAlias"
                      value={paymentAlias}
                      onChange={(e) => setPaymentAlias(e.target.value)}
                      placeholder="mi.alias"
                      className="pl-8"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="whatsapp">Número de WhatsApp *</Label>
                <Input
                  id="whatsapp"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  placeholder="Ej: +54 9 11 1234-5678"
                />
              </div>

              <div>
                <Label>Premios *</Label>
                {prizes.map((prize, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <Input
                      value={prize}
                      onChange={(e) => updatePrize(index, e.target.value)}
                      placeholder={`Premio ${index + 1}`}
                    />
                    {prizes.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removePrize(index)}
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  className="mt-2"
                  onClick={addPrize}
                >
                  <Plus className="mr-2" size={16} />
                  Agregar Premio
                </Button>
              </div>

              <div>
                <Label>Logo (opcional)</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="mt-2"
                />
                {logo && (
                  <div className="mt-2 relative w-32">
                    <img src={logo} alt="Logo" className="w-32 h-32 object-contain rounded border" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 w-6 h-6"
                      onClick={() => setLogo(null)}
                    >
                      <X size={12} />
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Label>Imágenes de Premios (opcional)</Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePrizeImageUpload}
                  className="mt-2"
                />
                {prizeImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {prizeImages.map((img, index) => (
                      <div key={index} className="relative">
                        <img src={img} alt={`Premio ${index + 1}`} className="w-20 h-20 object-cover rounded border" />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 w-6 h-6"
                          onClick={() => removePrizeImage(index)}
                        >
                          <X size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Columna derecha - Selección de clientes */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users size={20} />
                    Seleccionar Clientes
                  </CardTitle>
                  <CardDescription>
                    Cada cliente recibirá números proporcionales a sus compras
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAll}>
                    Todos
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={deselectAll}>
                    Ninguno
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {clientes.length === 0 ? (
                <div className="text-center py-8">
                  <Users size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No hay clientes con pagos registrados</p>
                </div>
              ) : (
                <>
                  {/* Filtros de búsqueda y orden */}
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mb-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <Input
                        type="text"
                        placeholder="Buscar por nombre o teléfono..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="cliente-search-input"
                      />
                    </div>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="min-w-[220px]" data-testid="cliente-sort-select">
                        <ArrowUpDown size={14} className="mr-1 text-gray-500" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="numeros_desc">Más números comprados</SelectItem>
                        <SelectItem value="numeros_asc">Menos números comprados</SelectItem>
                        <SelectItem value="monto_desc">Mayor monto pagado</SelectItem>
                        <SelectItem value="nombre_asc">Nombre (A-Z)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Resumen de selección */}
                  <div className="bg-purple-50 p-3 rounded-lg mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-purple-700">
                        <strong>{selectedClientes.length}</strong> clientes seleccionados
                      </span>
                      <span className="text-purple-700 font-bold">
                        {getTotalNumeros()} números
                      </span>
                    </div>
                    {unitPrice && (
                      <div className="text-green-700 font-bold text-lg">
                        Recaudación estimada: ${getTotalRecaudacion().toLocaleString('es-AR')}
                      </div>
                    )}
                  </div>

                  {/* Lista de clientes */}
                  <div className="max-h-[350px] overflow-y-auto space-y-2" data-testid="clientes-list">
                    {(() => {
                      const visibles = getVisibleClientes();
                      if (visibles.length === 0) {
                        return (
                          <p className="text-center text-gray-500 text-sm py-6">
                            No se encontraron clientes con ese filtro.
                          </p>
                        );
                      }
                      return visibles.map((cliente, index) => (
                      <div 
                        key={cliente.id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedClientes.includes(cliente.id)
                            ? 'bg-green-50 border-green-300'
                            : 'bg-white hover:bg-gray-50'
                        }`}
                        onClick={() => toggleCliente(cliente.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              selectedClientes.includes(cliente.id)
                                ? 'bg-green-500 border-green-500'
                                : 'border-gray-300'
                            }`}
                          >
                            {selectedClientes.includes(cliente.id) && (
                              <Check size={14} className="text-white" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              {index < 3 && (
                                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${
                                  index === 0 ? 'bg-yellow-400 text-yellow-900' :
                                  index === 1 ? 'bg-gray-300 text-gray-700' :
                                  'bg-amber-600 text-amber-100'
                                }`}>
                                  {index + 1}
                                </span>
                              )}
                              <span className="font-medium">{cliente.nombre}</span>
                            </div>
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <Phone size={12} />
                              {cliente.telefono}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-green-600 font-bold">
                            <Ticket size={16} />
                            {cliente.numeros_pagados} números
                          </div>
                          <div className="text-xs text-gray-500">
                            ${(cliente.monto_total_pagado || 0).toLocaleString('es-AR')} pagados
                          </div>
                        </div>
                      </div>
                      ));
                    })()}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Botón de crear */}
          <Button 
            type="submit" 
            className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
            disabled={creating || selectedClientes.length === 0}
          >
            {creating ? (
              <>
                <Loader2 className="animate-spin mr-2" size={20} />
                Creando Rifa...
              </>
            ) : (
              <>
                <Gift className="mr-2" size={20} />
                Crear Rifa ({getTotalNumeros()} números)
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <p className="text-sm text-blue-700">
            <strong>ℹ️ Cómo funciona:</strong> Cada cliente recibirá tantos números consecutivos como números haya pagado en rifas anteriores.
            Los números se asignan automáticamente ya marcados como PAGADOS.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateRifaClientes;
